import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Grid from '@mui/joy/Grid';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Stack from '@mui/joy/Stack';
import Chip from '@mui/joy/Chip';
import Avatar from '@mui/joy/Avatar';
import CircularProgress from '@mui/joy/CircularProgress';
import Link from '@mui/joy/Link';
import ButtonGroup from '@mui/joy/ButtonGroup';
import Button from '@mui/joy/Button';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { salesApi } from '../api/sales.api';
import { Sale } from '../types/sale';
import { LAYOUT } from '../theme/layoutTokens';
import DashboardTable from '../components/DashboardTable';
import SaleStatusChip from '../components/SaleStatusChip';

type Period = '7d' | '30d' | '90d' | '180d';

interface DashboardStats {
  totalVendido: number;
  completadas: number;
  pendientes: number;
  paidPending: number;
  canceladas: number;
  clientesActivos: number;
  ticketProm: number;
  topClientes: TopCliente[];
  topProductos: TopProducto[];
  recentSales: Sale[];
}

interface TopCliente {
  clientReference: string;
  name: string;
  ordenes: number;
  total: number;
}

interface TopProducto {
  name: string;
  qty: number;
  revenue: number;
}

const PERIOD_DAYS: Record<Period, number> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
  '180d': 180,
};

const PERIOD_LABELS: Record<Period, string> = {
  '7d': '7 días',
  '30d': '30 días',
  '90d': '90 días',
  '180d': '180 días',
};

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function getInitialsLocal(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function filterByPeriod(sales: Sale[], period: Period): Sale[] {
  const days = PERIOD_DAYS[period];
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  return sales.filter((s) => new Date(s.date) >= cutoff);
}

function computeStats(sales: Sale[]): DashboardStats {
  const completadas = sales.filter((s) => s.status === 'completed').length;
  const pendientes  = sales.filter((s) => s.status === 'pending').length;
  const paidPending = sales.filter((s) => s.status === 'paid_pending').length;
  const canceladas  = sales.filter((s) => s.status === 'cancelled').length;

  const activeSales = sales.filter((s) => s.status !== 'cancelled');
  const totalVendido = activeSales.reduce((sum, s) => sum + Number(s.total ?? 0), 0);
  const ticketProm = activeSales.length > 0 ? Math.round(totalVendido / activeSales.length) : 0;

  // Clients with at least one completed or pending sale
  const clientSet = new Set(
    sales
      .filter((s) => s.status === 'completed' || s.status === 'pending' || s.status === 'paid_pending')
      .map((s) => s.clientReference)
  );
  const clientesActivos = clientSet.size;

  // Top clients by total amount (non-cancelled)
  const clientMap = new Map<string, { name: string; ordenes: number; total: number }>();
  for (const s of activeSales) {
    if (!s.clientReference) continue;
    const existing = clientMap.get(s.clientReference);
    if (existing) {
      existing.ordenes += 1;
      existing.total += Number(s.total ?? 0);
    } else {
      clientMap.set(s.clientReference, {
        name: s.client?.name ?? s.clientReference,
        ordenes: 1,
        total: Number(s.total ?? 0),
      });
    }
  }
  const topClientes: TopCliente[] = Array.from(clientMap.entries())
    .map(([ref, v]) => ({ clientReference: ref, ...v }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 6);

  // Top products by units sold
  const productMap = new Map<string, { qty: number; revenue: number }>();
  for (const s of sales) {
    for (const p of s.saleProducts ?? s.products ?? []) {
      const name = ('product' in p && p.product?.name) ? p.product.name : (('name' in p) ? (p as { name: string }).name : 'Producto');
      const qty = p.quantity ?? 0;
      const price = Number(('price' in p) ? (p as { price: number }).price : 0);
      const existing = productMap.get(name);
      if (existing) {
        existing.qty += qty;
        existing.revenue += qty * price;
      } else {
        productMap.set(name, { qty, revenue: qty * price });
      }
    }
  }
  const topProductos: TopProducto[] = Array.from(productMap.entries())
    .map(([name, v]) => ({ name, ...v }))
    .sort((a, b) => b.qty - a.qty)
    .slice(0, 5);

  const recentSales = [...sales]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 5);

  return {
    totalVendido, completadas, pendientes, paidPending, canceladas,
    clientesActivos, ticketProm, topClientes, topProductos, recentSales,
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface KpiCardProps {
  icon: React.ReactNode;
  accentColor: string;
  value: React.ReactNode;
  label: string;
  sub?: React.ReactNode;
}

const KpiCard: React.FC<KpiCardProps> = ({ icon, accentColor, value, label, sub }) => (
  <Sheet
    variant="outlined"
    sx={{
      borderRadius: 'md',
      p: 2.5,
      boxShadow: 'sm',
      borderTop: `3px solid ${accentColor}`,
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
    }}
  >
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: 'sm',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: `${accentColor}18`,
        color: accentColor,
        mb: 0.5,
      }}
    >
      {icon}
    </Box>
    <Typography level="h2" sx={{ fontSize: '1.6rem', fontWeight: 'xl', letterSpacing: '-0.02em', lineHeight: 1.1 }}>
      {value}
    </Typography>
    <Typography level="body-sm" fontWeight="md" sx={{ color: 'neutral.600' }}>
      {label}
    </Typography>
    {sub && (
      <Typography level="body-xs" sx={{ color: 'neutral.400' }}>
        {sub}
      </Typography>
    )}
  </Sheet>
);

interface StatusBarProps {
  label: string;
  color: string;
  count: number;
  total: number;
}

const StatusBar: React.FC<StatusBarProps> = ({ label, color, count, total }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '140px 1fr 52px 44px', alignItems: 'center', gap: 1.5 }}>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
        <Typography level="body-sm" fontWeight="md">{label}</Typography>
      </Stack>
      <Box sx={{ height: 7, bgcolor: 'neutral.100', borderRadius: 99, overflow: 'hidden' }}>
        <Box
          sx={{
            height: '100%',
            width: `${pct}%`,
            bgcolor: color,
            borderRadius: 99,
            transition: 'width .5s cubic-bezier(.4,0,.2,1)',
          }}
        />
      </Box>
      <Typography level="body-sm" fontWeight="lg" sx={{ textAlign: 'right' }}>{count}</Typography>
      <Typography level="body-xs" sx={{ color: 'neutral.400', textAlign: 'right' }}>{pct}%</Typography>
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const Home: React.FC = () => {
  const [allSales, setAllSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<Period>('30d');

  useEffect(() => {
    const run = async (): Promise<void> => {
      try {
        setLoading(true);
        const res = await salesApi.getAll(1, 500);
        setAllSales(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Error cargando dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    void run();
  }, []);

  const filtered = useMemo(() => filterByPeriod(allSales, period), [allSales, period]);
  const stats = useMemo(() => computeStats(filtered), [filtered]);

  const pendientesTotal = stats.pendientes + stats.paidPending;
  const totalVentas = stats.completadas + stats.pendientes + stats.paidPending + stats.canceladas;
  const maxRevenue = stats.topProductos[0]?.revenue || 1;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: LAYOUT.contentPadding, maxWidth: LAYOUT.pageMaxWidth, mx: 'auto', width: '100%' }}>

      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }} flexWrap="wrap" gap={2}>
        <Box>
          <Typography level="h1" sx={{ fontSize: { xs: '1.35rem', sm: '1.5rem', md: '1.75rem' }, fontWeight: 'xl', letterSpacing: '-0.02em' }}>
            Inicio
          </Typography>
          <Typography level="body-sm" sx={{ color: 'neutral.500', mt: 0.25 }}>
            Resumen general del negocio
          </Typography>
        </Box>

        <ButtonGroup size="sm" variant="outlined" color="neutral">
          {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
            <Button
              key={p}
              variant={period === p ? 'solid' : 'outlined'}
              color={period === p ? 'primary' : 'neutral'}
              onClick={() => setPeriod(p)}
            >
              {PERIOD_LABELS[p]}
            </Button>
          ))}
        </ButtonGroup>
      </Stack>

      {/* KPI cards */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>

        <Grid xs={12} sm={6} md={3}>
          <KpiCard
            accentColor="#3b82f6"
            icon={<PeopleAltOutlinedIcon fontSize="small" />}
            value={stats.clientesActivos}
            label="Clientes activos"
            sub="Con al menos 1 venta en el período"
          />
        </Grid>

        <Grid xs={12} sm={6} md={3}>
          <Sheet
            variant="outlined"
            sx={{
              borderRadius: 'md',
              p: 2.5,
              boxShadow: 'sm',
              borderTop: '3px solid #f59e0b',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Box sx={{ width: 36, height: 36, borderRadius: 'sm', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'warning.softBg', color: 'warning.plainColor', mb: 0.5 }}>
              <AccessTimeOutlinedIcon fontSize="small" />
            </Box>
            <Typography
              level="h2"
              sx={{
                fontSize: '1.6rem',
                fontWeight: 'xl',
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
                color: pendientesTotal > 0 ? 'warning.plainColor' : 'success.plainColor',
              }}
            >
              {pendientesTotal}
            </Typography>
            <Typography level="body-sm" fontWeight="md" sx={{ color: 'neutral.600' }}>
              Ventas pendientes
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
              <Chip size="sm" variant="soft" color="warning" sx={{ fontSize: '11px' }}>
                {stats.pendientes} sin pago
              </Chip>
              <Chip size="sm" variant="soft" color="primary" sx={{ fontSize: '11px' }}>
                {stats.paidPending} por entregar
              </Chip>
            </Stack>
          </Sheet>
        </Grid>

        <Grid xs={12} sm={6} md={3}>
          <KpiCard
            accentColor="#25D366"
            icon={<AttachMoneyIcon fontSize="small" />}
            value={formatCurrency(stats.totalVendido)}
            label="Total vendido"
            sub={`${stats.completadas} ventas completadas`}
          />
        </Grid>

        <Grid xs={12} sm={6} md={3}>
          <KpiCard
            accentColor="#9aa5b4"
            icon={<TrendingUpIcon fontSize="small" />}
            value={formatCurrency(stats.ticketProm)}
            label="Ticket promedio"
            sub="Por venta en el período"
          />
        </Grid>
      </Grid>

      {/* Main grid */}
      <Grid container spacing={2}>

        {/* Columna principal */}
        <Grid xs={12} lg={8}>
          <Stack spacing={2}>

            {/* Estado de ventas */}
            <Sheet variant="outlined" sx={{ borderRadius: 'md', boxShadow: 'sm', overflow: 'hidden' }}>
              <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography level="title-md" fontWeight="lg">Estado de ventas</Typography>
                <Typography level="body-xs" sx={{ color: 'neutral.500' }}>{totalVentas} ventas en el período</Typography>
              </Box>
              <Box sx={{ px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <StatusBar label="Completadas"  color="#25D366" count={stats.completadas} total={totalVentas} />
                <StatusBar label="Sin pago"     color="#f59e0b" count={stats.pendientes}  total={totalVentas} />
                <StatusBar label="Por entregar" color="#3b82f6" count={stats.paidPending} total={totalVentas} />
                <StatusBar label="Canceladas"   color="#c41c1c" count={stats.canceladas}  total={totalVentas} />
              </Box>
            </Sheet>

            {/* Ventas recientes */}
            <Sheet variant="outlined" sx={{ borderRadius: 'md', boxShadow: 'sm', overflow: 'hidden' }}>
              <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography level="title-md" fontWeight="lg">Ventas recientes</Typography>
                <Link component={RouterLink} to="/sales" level="body-sm" fontWeight="lg" underline="none" sx={{ color: 'primary.plainColor' }}>
                  Ver todas →
                </Link>
              </Box>
              <DashboardTable>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Cliente</th>
                    <th>Fecha</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recentSales.length === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--joy-palette-neutral-500)' }}>Sin ventas en el período</td></tr>
                  ) : (
                    stats.recentSales.map((s) => (
                      <tr key={s.reference}>
                        <td>
                          <Typography level="body-xs" sx={{ fontFamily: 'code', color: 'neutral.500' }}>
                            #{s.reference.slice(-8).toUpperCase()}
                          </Typography>
                        </td>
                        <td>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar size="sm" variant="soft" color="primary" sx={{ '--Avatar-size': '24px', fontSize: '10px', fontWeight: 'lg' }}>
                              {getInitialsLocal(s.client?.name ?? '?')}
                            </Avatar>
                            <Typography level="body-sm" fontWeight="md">{s.client?.name ?? '—'}</Typography>
                          </Stack>
                        </td>
                        <td>
                          <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                            {new Date(s.date).toLocaleDateString('es-AR')}
                          </Typography>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <Typography level="body-sm" fontWeight="lg">{formatCurrency(Number(s.total))}</Typography>
                        </td>
                        <td><SaleStatusChip status={s.status} /></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </DashboardTable>
            </Sheet>

            {/* Clientes que más compraron */}
            <Sheet variant="outlined" sx={{ borderRadius: 'md', boxShadow: 'sm', overflow: 'hidden' }}>
              <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography level="title-md" fontWeight="lg">Clientes que más compraron</Typography>
                <Typography level="body-xs" sx={{ color: 'neutral.500' }}>Por monto total en el período</Typography>
              </Box>
              <DashboardTable>
                <thead>
                  <tr>
                    <th style={{ width: 32 }}>#</th>
                    <th>Cliente</th>
                    <th>Pedidos</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.topClientes.length === 0 ? (
                    <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--joy-palette-neutral-500)' }}>Sin datos</td></tr>
                  ) : (
                    stats.topClientes.map((c, i) => {
                      const rankBg  = i === 0 ? '#fef3c7' : i === 1 ? 'var(--joy-palette-neutral-100)' : i === 2 ? '#fff7ed' : 'var(--joy-palette-neutral-100)';
                      const rankClr = i === 0 ? '#b45309' : i === 1 ? 'var(--joy-palette-neutral-600)' : i === 2 ? '#c2410c' : 'var(--joy-palette-neutral-400)';
                      return (
                        <tr key={c.clientReference}>
                          <td>
                            <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: rankBg, color: rankClr, fontSize: '10px', fontWeight: 'xl', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {i + 1}
                            </Box>
                          </td>
                          <td>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Avatar size="sm" variant="soft" color="primary" sx={{ '--Avatar-size': '26px', fontSize: '10px', fontWeight: 'lg' }}>
                                {getInitialsLocal(c.name)}
                              </Avatar>
                              <Typography level="body-sm" fontWeight="md">{c.name}</Typography>
                            </Stack>
                          </td>
                          <td><Typography level="body-xs" sx={{ color: 'neutral.500' }}>{c.ordenes}</Typography></td>
                          <td style={{ textAlign: 'right' }}>
                            <Typography level="body-sm" fontWeight="xl" sx={{ color: 'primary.700' }}>
                              {formatCurrency(c.total)}
                            </Typography>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </DashboardTable>
            </Sheet>
          </Stack>
        </Grid>

        {/* Columna lateral */}
        <Grid xs={12} lg={4}>
          <Stack spacing={2}>

            {/* Productos más vendidos */}
            <Sheet variant="outlined" sx={{ borderRadius: 'md', boxShadow: 'sm', overflow: 'hidden' }}>
              <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography level="title-md" fontWeight="lg">Productos más vendidos</Typography>
                <Typography level="body-xs" sx={{ color: 'neutral.500' }}>Por unidades vendidas</Typography>
              </Box>
              <Box sx={{ px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {stats.topProductos.length === 0 ? (
                  <Typography level="body-sm" sx={{ color: 'neutral.500' }}>Sin datos</Typography>
                ) : (
                  stats.topProductos.map((p, i) => {
                    const rankBg  = i === 0 ? '#fef3c7' : i === 1 ? 'var(--joy-palette-neutral-100)' : i === 2 ? '#fff7ed' : 'var(--joy-palette-neutral-100)';
                    const rankClr = i === 0 ? '#b45309' : i === 1 ? 'var(--joy-palette-neutral-600)' : i === 2 ? '#c2410c' : 'var(--joy-palette-neutral-400)';
                    return (
                      <Box key={p.name} sx={{ display: 'grid', gridTemplateColumns: '20px 1fr 48px', alignItems: 'center', gap: 1.25 }}>
                        <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: rankBg, color: rankClr, fontSize: '9px', fontWeight: 'xl', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {i + 1}
                        </Box>
                        <Box>
                          <Typography level="body-sm" fontWeight="md" noWrap>{p.name}</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Box sx={{ flex: 1, height: 5, bgcolor: 'neutral.100', borderRadius: 99, overflow: 'hidden' }}>
                              <Box sx={{ height: '100%', width: `${Math.round((p.revenue / maxRevenue) * 100)}%`, bgcolor: 'primary.400', borderRadius: 99 }} />
                            </Box>
                            <Typography level="body-xs" sx={{ color: 'neutral.400', whiteSpace: 'nowrap' }}>{p.qty} ud.</Typography>
                          </Box>
                        </Box>
                        <Typography level="body-xs" fontWeight="xl" sx={{ textAlign: 'right', color: 'neutral.700' }}>
                          {formatCurrency(p.revenue)}
                        </Typography>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Sheet>

          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Home;
